import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, ArrowRight, Globe, Award, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

import { SITE_URL } from '../components/GlobalSchema';
const About = () => {
  const stats = [
    { icon: <Users className="w-8 h-8" />, value: '65+', label: 'Years Combined Experience' },
    { icon: <Globe className="w-8 h-8" />, value: 'Global', label: 'International Network' },
    { icon: <Award className="w-8 h-8" />, value: 'Expert', label: 'Financial Solutions' },
    { icon: <TrendingUp className="w-8 h-8" />, value: 'Proven', label: 'Track Record' }
  ];

  const expertise = [
    'Alternative Finance Solutions',
    'Standby Letter of Credit (SBLC) Expertise',
    'Bank Guarantee (BG) Solutions',
    'Project Finance and Capital Provision',
    'Strategic Capitalization Services',
    'Global Business Relationships',
    'Credit Enhancement Programs',
    'Payment Guarantee Services'
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About ABL Finance | 65+ Years in SBLC & Trade Finance</title>
        <meta name="description" content="ABL Finance (WBA UK Holdings 1) is a London consultancy with 65+ years combined experience in SBLC, Bank Guarantees and Trade Finance since 2008." />
        <meta name="keywords" content="about ABL Finance, financial consultancy London, SBLC experts, bank guarantee specialists, alternative finance company, trade finance consultants, financial advisory firm UK" />
        <meta property="og:title" content="About ABL Finance | 65+ Years in SBLC & Trade Finance" />
        <meta property="og:description" content="ABL Finance (WBA UK Holdings 1) is a London consultancy with 65+ years combined experience in SBLC, Bank Guarantees and Trade Finance since 2008." />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ABL Finance" />
        <meta property="og:url" content={`${SITE_URL}/about`} />
        <meta property="og:image" content={`${SITE_URL}/logo.png`} />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About ABL Finance | 65+ Years in SBLC & Trade Finance" />
        <meta name="twitter:description" content="ABL Finance (WBA UK Holdings 1) is a London consultancy with 65+ years combined experience in SBLC, Bank Guarantees and Trade Finance since 2008." />
        <meta name="twitter:image" content={`${SITE_URL}/logo.png`} />
        <link rel="canonical" href={`${SITE_URL}/about`} />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "mainEntity": {
              "@type": "Organization",
              "name": "ABL Finance",
              "logo": "/logo.png",
              "foundingDate": "2008",
              "numberOfEmployees": "50-100",
              "description": "Globally-minded financial consultancy with an impressive track record of alternative finance, specializing in SBLC and Bank Guarantee solutions.",
              "knowsAbout": ["Standby Letter of Credit", "Bank Guarantees", "Trade Finance", "Risk Management", "Corporate Finance", "Asset Management"]
            }
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Home", "item": "${SITE_URL}/"},
              {"@type": "ListItem", "position": 2, "name": "About", "item": "${SITE_URL}/about"}
            ]
          }
        `}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-slate-900 to-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About ABL Finance</h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            Your trusted partner in alternative finance and strategic financial solutions
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-slate-900 mb-2">{stat.value}</div>
                <div className="text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main About Content */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Who We Are</h2>
              <div className="bg-emerald-50 border-l-4 border-emerald-600 p-4 mb-6">
                <p className="text-lg font-semibold text-slate-900">WBA UK HOLDINGS 1 LIMITED</p>
                <p className="text-sm text-slate-600">Trading as ABL Finance | Registered in England and Wales</p>
              </div>
              <p className="text-lg text-slate-600 mb-6">
                ABL Finance is globally-minded with an impressive track record of alternative finance. We aim not only at providing capital for businesses but also at building up successful businesses together.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                We fulfill this commitment by providing distinctive services on strategic capitalization around the world. This success has contributed to our social capital, and we boast of business relationships across the globe.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                Our only focus is on creating lasting value through building strong client-centric relationships and fully committing ourselves to their success by delivering on their financial needs.
              </p>
              <p className="text-lg text-slate-600">
                We take a proactive approach to our work, promoting collaboration throughout our clients' organizations. We understand that our main goal is to make a real and sustainable impact, and we firmly believe that achieving this relies on close cooperation with our clients.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1709715357520-5e1047a2b691?w=800&q=80&auto=format&fit=crop"
                alt="Team Collaboration"
                className="rounded-lg shadow-2xl"
                loading="lazy"
                width="665"
                height="443"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?w=800&q=80&auto=format&fit=crop"
                alt="Professional Meeting"
                className="rounded-lg shadow-2xl"
                loading="lazy"
                width="665"
                height="443"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Expertise</h2>
              <p className="text-lg text-slate-600 mb-8">
                We take great pride in our experience in the sector of alternative financing, especially by utilizing Standby Letter of Credit (SBLC) and Bank Guarantee (BG). This credit is to our dynamic team with over 100 collective years of experience providing best-in-class consulting and advisory services.
              </p>
              <ul className="space-y-3">
                {expertise.map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0 mt-1" />
                    <span className="text-lg text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Our Mission</h2>
            <p className="text-xl text-gray-300 mb-8">
              We exist to offer innovative options to solve your financing needs irrespective of your jurisdiction. We are committed to sharing the extensive knowledge from our carefully curated international network with you.
            </p>
            <p className="text-xl text-gray-300 mb-8">
              This allows us to create customized solutions that empower you to progress when you choose us as your partner to solve your funding needs.
            </p>
            <p className="text-xl text-gray-300">
              We prioritize independence to avoid conflicts of interest. This enables us to offer flexible and entrepreneurial solutions that are transparent and tailored to your specific needs.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 text-center mb-16">Our Core Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-emerald-500 transition-colors">
              <CardHeader>
                <CardTitle className="text-2xl">Transparency</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Transparency is a fundamental principle that guides our interactions and operations. We believe in open and honest communication, providing clear and accurate information to our clients and stakeholders.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-emerald-500 transition-colors">
              <CardHeader>
                <CardTitle className="text-2xl">Integrity</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  We adhere to the highest ethical standards, maintaining transparency and confidentiality in all our interactions. Our commitment to integrity builds trust with our clients.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-emerald-500 transition-colors">
              <CardHeader>
                <CardTitle className="text-2xl">Accountability</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  We uphold the value of accountability in all aspects of our operations. We take responsibility for our actions, decisions, and commitments, ensuring that we deliver on our promises.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-emerald-500 transition-colors">
              <CardHeader>
                <CardTitle className="text-2xl">Excellence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  We strive for excellence in everything we do, aiming to exceed client expectations through innovative financial solutions and superior service delivery. Every client is unique.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-emerald-500 transition-colors">
              <CardHeader>
                <CardTitle className="text-2xl">Value Creation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  We are dedicated to creating value for our clients and stakeholders. Through our innovative solutions and strategic partnerships, we aim to maximize value and help achieve financial goals.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-emerald-500 transition-colors">
              <CardHeader>
                <CardTitle className="text-2xl">Expert Guidance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Our team consists of experienced professionals with in-depth knowledge of financial markets and instruments, ensuring our clients receive expert and impartial guidance.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Partner With Us</h2>
          <p className="text-xl mb-8">
            Ready to take the leap into a realm of innovative finance? Contact us today to explore how our strategic solutions can redefine your financial landscape.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Get In Touch
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Last Updated */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <p className="text-sm text-slate-500 text-right">Last updated: May 2026</p>
      </div>
    </div>
  );
};

export default About;